import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Button, Alert, Spinner, Form, Modal, Dropdown } from 'react-bootstrap';
import {
  getMarketingEmailContacts,
  getMarketingEmailContactsExport,
  postMarketingEmailContacts,
  postMarketingEmailContactsBulkImport,
  deleteMarketingEmailContacts,
  patchMarketingEmailContactsId,
  useGetUsersId,
  useGetMarketingEmailLists,
  getGetMarketingEmailListsQueryKey,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import type { JsonPatchOperation, EmailContactSummary } from '../api/generated/model';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import type { EmailContact } from '../api/generated/model';
import { ListSelector } from '../components/ListSelector';
import { ResubscribeConfirmModal } from '../components/ResubscribeConfirmModal';
import { userPreferencesService } from '../services/userPreferencesService';

// Import DataTable system
import { DataTable } from '../components/common/DataTable';
import type { ColumnConfig } from '../components/common/DataTable/types';

type EmailContactColumnKey = 'email' | 'id' | 'unsubscribed' | 'created_at' | 'first_name' | 'last_name' | 'lists' | 'address_line_1' | 'address_line_2' | 'city' | 'state_province_region' | 'postal_code';

const EmailContactsTab: React.FC = () => {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [emailFilter, setEmailFilter] = useState<string>('');
  const [debouncedEmailFilter, setDebouncedEmailFilter] = useState<string>('');
  const [listFilter, setListFilter] = useState<string[]>([]);
  const [listMatchMode, setListMatchMode] = useState<'any' | 'all'>('any');
  const [statusFilter, setStatusFilter] = useState<'all' | 'subscribed' | 'unsubscribed'>('all');
  const [sortField, setSortField] = useState<EmailContactColumnKey>('email');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [editingCell, setEditingCell] = useState<{ id: string; column: EmailContactColumnKey } | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [showResubscribeConfirm, setShowResubscribeConfirm] = useState(false);
  const [pendingResubscribe, setPendingResubscribe] = useState<{ id: string; email: string } | null>(null);
  const [showEditorResubscribeConfirm, setShowEditorResubscribeConfirm] = useState(false);
  const [originalEditorData, setOriginalEditorData] = useState<{
    id: string;
    unsubscribed: boolean;
    first_name: string;
    last_name: string;
    address_line_1: string;
    address_line_2: string;
    city: string;
    state_province_region: string;
    postal_code: string;
    lists: Array<{ id?: string; name?: string }>;
  } | null>(null);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [editorMode, setEditorMode] = useState<'create' | 'edit'>('create');
  const [editorData, setEditorData] = useState<{
    id?: string;
    email: string;
    unsubscribed: boolean;
    first_name: string;
    last_name: string;
    address_line_1: string;
    address_line_2: string;
    city: string;
    state_province_region: string;
    postal_code: string;
    lists: Array<{ id?: string; name?: string }>;
  }>({
    email: '',
    unsubscribed: false,
    first_name: '',
    last_name: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state_province_region: '',
    postal_code: '',
    lists: [],
  });

  // Email Lists state
  const [modalSuccess, setModalSuccess] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);

  // CSV Upload Modal state
  const [showCsvUploadModal, setShowCsvUploadModal] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvUploadLists, setCsvUploadLists] = useState<Array<{ id?: string; name?: string }>>([]);
  const [csvUploadError, setCsvUploadError] = useState<string | null>(null);

  // Import Info Modal state
  const [showImportInfoModal, setShowImportInfoModal] = useState(false);

  // Export Modal state
  const [showExportModal, setShowExportModal] = useState(false);

  // Export field selection state
  const exportFieldOptions = [
    { key: 'email', default: true },
    { key: 'id', default: false },
    { key: 'unsubscribed', default: false },
    { key: 'first_name', default: true },
    { key: 'last_name', default: true },
    { key: 'address_line_1', default: false },
    { key: 'address_line_2', default: false },
    { key: 'city', default: false },
    { key: 'state_province_region', default: false },
    { key: 'postal_code', default: false },
  ];
  const [exportFields, setExportFields] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    exportFieldOptions.forEach(f => { initial[f.key] = f.default; });
    return initial;
  });
  // Custom column names - empty string means use database field name
  const [exportFieldNames, setExportFieldNames] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    exportFieldOptions.forEach(f => { initial[f.key] = ''; });
    return initial;
  });
  const [exportFieldNamesLoaded, setExportFieldNamesLoaded] = useState(false);
  // Store original names when modal opens for cancel/restore
  const [originalExportFieldNames, setOriginalExportFieldNames] = useState<Record<string, string>>({});

  // Delete confirmation state (for single contact delete in editor modal)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);


  // Bulk delete modal state
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState('');

  const queryClient = useQueryClient();

  // Fetch all email lists
  const { data: emailLists } = useGetMarketingEmailLists();

  const { user } = useSelector((state: RootState) => state.auth);

  // Fixed limit for infinite scroll
  const FETCH_LIMIT = 50;


  // Debounce email filter to avoid spamming API while typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmailFilter(emailFilter);
    }, 300);
    return () => clearTimeout(timer);
  }, [emailFilter]);

  // Fetch user preferences
  const { data: userData } = useGetUsersId(user?.id || '', {
    query: {
      enabled: !!user?.id,
    }
  });

  // Load saved export field name mappings from user preferences
  useEffect(() => {
    if (userData?.preferences?.export_field_mappings?.email_contacts && !exportFieldNamesLoaded) {
      const savedMappings = userData.preferences.export_field_mappings.email_contacts;
      setExportFieldNames(prev => {
        const updated = { ...prev };
        Object.keys(savedMappings).forEach(key => {
          if (key in updated) {
            updated[key] = savedMappings[key] || '';
          }
        });
        return updated;
      });
      setExportFieldNamesLoaded(true);
    }
  }, [userData?.preferences?.export_field_mappings, exportFieldNamesLoaded]);

  // Save export field name mappings to user preferences
  const saveExportFieldMappings = useCallback(async (mappings: Record<string, string>) => {
    if (!user?.id) return;
    try {
      await userPreferencesService.updateExportFieldMappings(user.id, 'email_contacts', mappings);
    } catch (err) {
      console.error('Failed to save export field mappings:', err);
    }
  }, [user?.id]);

  // Row identity. Shared with the hook's duplicate-detection and DataTable.
  const getRowKey = useCallback((contact: EmailContact) => contact.id || '', []);

  // Build query params - for cursor-based pagination, only send cursor + limit
  const buildQueryParams = useCallback(
    (cursor: string | undefined) =>
      cursor
        ? { cursor, limit: FETCH_LIMIT }
        : {
            email: debouncedEmailFilter || undefined,
            list_id: listFilter.length > 0 ? listFilter.join(',') : undefined,
            list_match: listFilter.length > 1 ? listMatchMode : undefined,
            subscribed: statusFilter !== 'all' ? statusFilter : undefined,
            sort_field: sortField as any,
            sort_direction: sortDirection as any,
            limit: FETCH_LIMIT,
          },
    [debouncedEmailFilter, listFilter, listMatchMode, statusFilter, sortField, sortDirection]
  );

  const {
    allItems: subscriptions,
    totalCount,
    apiSummary: summary,
    isLoading,
    error,
    hasMore,
    handleLoadMore,
    setAllItems: setAllContacts,
  } = usePaginatedQuery<EmailContact, Awaited<ReturnType<typeof getMarketingEmailContacts>>, EmailContactSummary>({
    queryKey: 'emailSubscriptions',
    buildQueryParams,
    queryFn: (params) => getMarketingEmailContacts(params as any),
    getRowKey,
  });

  // Refetch when component mounts (tab becomes active)
  useEffect(() => {
    setAllContacts([]);
    queryClient.invalidateQueries({ queryKey: ['emailSubscriptions'] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bulk import mutation
  const bulkImportMutation = useMutation({
    mutationFn: ({ file, listIds, newListNames }: { file: File; listIds?: string[]; newListNames?: string[] }) => {
      return postMarketingEmailContactsBulkImport({
        file,
        list_ids: listIds?.length ? listIds.join(',') : undefined,
        new_list_names: newListNames?.length ? newListNames.join(',') : undefined,
      });
    },
    onSuccess: (response: any) => {
      const { imported, created, updated, unchanged } = response;
      setUploadSuccess(
        `Successfully imported ${imported} email${imported !== 1 ? 's' : ''} (${created} new, ${updated} updated, ${unchanged} unchanged)`
      );
      setUploadError(null);
      setCsvUploadError(null);
      setShowCsvUploadModal(false);
      setShowImportInfoModal(false);
      setCsvFile(null);
      setCsvUploadLists([]);
      queryClient.invalidateQueries({ queryKey: ['emailSubscriptions'] });
      queryClient.invalidateQueries({ queryKey: getGetMarketingEmailListsQueryKey() });
    },
    onError: (error: any) => {
      const apiError = error?.response?.data?.message || error?.message || 'Failed to import CSV';
      // Show error in modal instead of main page
      setCsvUploadError(apiError);
    },
  });

  // Update subscription mutation (uses JSON Patch format)
  const updateSubscriptionMutation = useMutation({
    mutationFn: ({ id, operations }: { id: string; operations: JsonPatchOperation[] }) => {
      return patchMarketingEmailContactsId(id, operations);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailSubscriptions'] });
      queryClient.invalidateQueries({ queryKey: getGetMarketingEmailListsQueryKey() });
      setEditingCell(null);
      setEditValue('');
    },
    onError: (error: any) => {
      const apiError = error?.response?.data?.message || error?.message || 'Failed to update subscription';
      setUploadError(apiError);
    },
  });

  // Create subscription mutation
  const createSubscriptionMutation = useMutation({
    mutationFn: (data: any) => {
      return postMarketingEmailContacts(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailSubscriptions'] });
      // Reset pagination to refresh data
      setAllContacts([]);
      setShowEditorModal(false);
      setUploadSuccess('Contact added successfully');
    },
    onError: (error: any) => {
      const apiError = error?.response?.data?.message || error?.message || 'Failed to create subscription';
      setUploadError(apiError);
    },
  });

  // Delete contacts mutation (filter-based)
  const deleteContactsMutation = useMutation({
    mutationFn: (params: {
      email?: string;
      list_id?: string;
      list_match?: 'any' | 'all';
      subscribed?: 'all' | 'subscribed' | 'unsubscribed';
    }) => deleteMarketingEmailContacts(params),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['emailSubscriptions'] });
      queryClient.invalidateQueries({ queryKey: getGetMarketingEmailListsQueryKey() });
      // Reset pagination to refresh data
      setAllContacts([]);
      setShowBulkDeleteModal(false);
      setDeleteAllConfirmText('');
      setUploadSuccess(`${response.deleted || 0} contact${(response.deleted || 0) !== 1 ? 's' : ''} deleted successfully`);
    },
    onError: (error: any) => {
      const apiError = error?.response?.data?.message || error?.message || 'Failed to delete contacts';
      setUploadError(apiError);
    },
  });

  // Delete single contact mutation (for editor modal) - uses filter-based delete with specific email
  const deleteSingleContactMutation = useMutation({
    mutationFn: (email: string) => deleteMarketingEmailContacts({ email }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailSubscriptions'] });
      queryClient.invalidateQueries({ queryKey: getGetMarketingEmailListsQueryKey() });
      // Reset pagination to refresh data
      setAllContacts([]);
      setShowEditorModal(false);
      setUploadSuccess('Contact deleted successfully');
    },
    onError: (error: any) => {
      const apiError = error?.response?.data?.message || error?.message || 'Failed to delete contact';
      setModalError(apiError);
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setUploadError('Please upload a CSV file');
      return;
    }

    setCsvFile(file);
    setCsvUploadLists([]);
    setCsvUploadError(null);
    setShowCsvUploadModal(true);
    event.target.value = '';
  };

  const handleCsvUpload = () => {
    if (!csvFile) return;
    setCsvUploadError(null);
    // Extract list IDs and new list names from csvUploadLists
    const listIds = csvUploadLists.filter(l => l.id).map(l => l.id!);
    const newListNames = csvUploadLists.filter(l => !l.id && l.name).map(l => l.name!);
    bulkImportMutation.mutate({
      file: csvFile,
      listIds,
      newListNames,
    });
  };

  // Build export params from field selection state
  const getExportFieldParams = () => {
    const selectedFields = exportFieldOptions
      .filter(f => exportFields[f.key])
      .map(f => f.key);
    // Use custom name if set, otherwise use database field name
    const customNames = selectedFields.map(key => exportFieldNames[key] || key);

    return {
      fields: selectedFields.join(','),
      field_names: customNames.join(','),
    };
  };

  const handleExportFiltered = async () => {
    try {
      const fieldParams = getExportFieldParams();
      const response = await getMarketingEmailContactsExport({
        email: debouncedEmailFilter || undefined,
        list_id: listFilter.length > 0 ? listFilter.join(',') : undefined,
        list_match: listFilter.length > 1 ? listMatchMode : undefined,
        subscribed: statusFilter !== 'all' ? statusFilter : undefined,
        fields: fieldParams.fields,
        field_names: fieldParams.field_names,
      });
      const blob = new Blob([response as any], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const timestamp = new Date().toISOString().replace(/:/g, '-').split('.')[0];
      a.download = `email-contacts-filtered-${timestamp}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      const apiError = error?.response?.data?.message || error?.message || 'Failed to export CSV';
      setUploadError(apiError);
    }
  };

  const stats = {
    total: summary?.total ?? totalCount ?? subscriptions.length,
    subscribed: summary?.subscribed ?? subscriptions.filter((s: any) => !s.unsubscribed).length,
    unsubscribed: summary?.unsubscribed ?? subscriptions.filter((s: any) => s.unsubscribed).length,
  };

  // Handle sort change from DataTable
  const handleSortChange = useCallback((field: string, direction: 'asc' | 'desc') => {
    setSortField(field as EmailContactColumnKey);
    setSortDirection(direction);
  }, []);

  // Editable columns
  const editableColumns: EmailContactColumnKey[] = [
    'first_name', 'last_name', 'address_line_1', 'address_line_2',
    'city', 'state_province_region', 'postal_code'
  ];

  // Start editing a cell
  const handleCellClick = (subscription: EmailContact, columnKey: EmailContactColumnKey) => {
    if (!editableColumns.includes(columnKey)) return;

    const currentValue = getCellText(subscription, columnKey);
    setEditingCell({ id: subscription.id || '', column: columnKey });
    setEditValue(currentValue);
  };

  // Save edit
  const handleSaveEdit = () => {
    if (!editingCell) return;

    const operations: JsonPatchOperation[] = [
      { op: 'replace', path: `/${editingCell.column}`, value: editValue || null },
    ];

    updateSubscriptionMutation.mutate({
      id: editingCell.id,
      operations,
    });
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Handle Enter key to save, Escape to cancel
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Handle status change
  const handleStatusClick = (subscription: EmailContact) => {
    const newStatus = !subscription.unsubscribed;

    // If resubscribing (currently unsubscribed, want to subscribe), show confirmation
    if (subscription.unsubscribed && !newStatus) {
      setPendingResubscribe({
        id: subscription.id || '',
        email: subscription.email || '',
      });
      setShowResubscribeConfirm(true);
    } else {
      // Unsubscribing doesn't need confirmation
      updateSubscriptionMutation.mutate({
        id: subscription.id || '',
        operations: [{ op: 'replace', path: '/unsubscribed', value: newStatus }],
      });
    }
  };

  // Confirm resubscribe
  const handleConfirmResubscribe = () => {
    if (pendingResubscribe) {
      updateSubscriptionMutation.mutate({
        id: pendingResubscribe.id,
        operations: [{ op: 'replace', path: '/unsubscribed', value: false }],
      });
    }
    setShowResubscribeConfirm(false);
    setPendingResubscribe(null);
  };

  // Cancel resubscribe
  const handleCancelResubscribe = () => {
    setShowResubscribeConfirm(false);
    setPendingResubscribe(null);
  };

  // Open editor modal for creating new contact
  const handleAddContact = () => {
    setEditorMode('create');
    setEditorData({
      email: '',
      unsubscribed: false,
      first_name: '',
      last_name: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state_province_region: '',
      postal_code: '',
      lists: [],
    });
    setShowEditorModal(true);
  };

  // Open editor modal for editing existing contact
  const handleEditContact = (subscription: EmailContact) => {
    setEditorMode('edit');
    setShowDeleteConfirm(false);
    setShowEditorResubscribeConfirm(false);
    // Sort lists alphabetically by name on initial load
    const sortedLists = [...(subscription.lists || [])].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    );
    // Track original data for change detection and resubscribe confirmation
    const originalData = {
      id: subscription.id || '',
      unsubscribed: subscription.unsubscribed || false,
      first_name: subscription.first_name || '',
      last_name: subscription.last_name || '',
      address_line_1: subscription.address_line_1 || '',
      address_line_2: subscription.address_line_2 || '',
      city: subscription.city || '',
      state_province_region: subscription.state_province_region || '',
      postal_code: subscription.postal_code || '',
      lists: sortedLists,
    };
    setOriginalEditorData(originalData);
    setEditorData({
      email: subscription.email || '',
      ...originalData,
    });
    setShowEditorModal(true);
  };

  // Check if resubscription confirmation is needed
  const isResubscribing = editorMode === 'edit' && originalEditorData?.unsubscribed && !editorData.unsubscribed;

  // Helper to compare lists for equality
  const listsEqual = (a: Array<{ id?: string; name?: string }>, b: Array<{ id?: string; name?: string }>) => {
    if (a.length !== b.length) return false;
    const aIds = a.map(l => l.id).sort();
    const bIds = b.map(l => l.id).sort();
    return aIds.every((id, i) => id === bIds[i]);
  };

  // Save editor modal
  const handleSaveEditor = async () => {
    // Check if we're resubscribing and need confirmation
    if (isResubscribing && !showEditorResubscribeConfirm) {
      setShowEditorResubscribeConfirm(true);
      return;
    }

    if (editorMode === 'create') {
      createSubscriptionMutation.mutate({
        email: editorData.email,
        unsubscribed: editorData.unsubscribed,
        first_name: editorData.first_name || undefined,
        last_name: editorData.last_name || undefined,
        address_line_1: editorData.address_line_1 || undefined,
        address_line_2: editorData.address_line_2 || undefined,
        city: editorData.city || undefined,
        state_province_region: editorData.state_province_region || undefined,
        postal_code: editorData.postal_code || undefined,
      });
      // Note: List memberships for new contacts will be handled after we have the subscription ID
      // This is a limitation of the current flow - we'd need to refactor to get the ID from the mutation response
    } else {
      // For edit mode, only include changed fields in PATCH operations
      const operations: JsonPatchOperation[] = [];

      if (originalEditorData) {
        // ID change must be processed last by the backend (it changes the resource identity)
        if (editorData.id !== originalEditorData.id) {
          operations.push({ op: 'replace', path: '/id', value: editorData.id });
        }
        if (editorData.unsubscribed !== originalEditorData.unsubscribed) {
          operations.push({ op: 'replace', path: '/unsubscribed', value: editorData.unsubscribed });
        }
        if (editorData.first_name !== originalEditorData.first_name) {
          operations.push({ op: 'replace', path: '/first_name', value: editorData.first_name || null });
        }
        if (editorData.last_name !== originalEditorData.last_name) {
          operations.push({ op: 'replace', path: '/last_name', value: editorData.last_name || null });
        }
        if (editorData.address_line_1 !== originalEditorData.address_line_1) {
          operations.push({ op: 'replace', path: '/address_line_1', value: editorData.address_line_1 || null });
        }
        if (editorData.address_line_2 !== originalEditorData.address_line_2) {
          operations.push({ op: 'replace', path: '/address_line_2', value: editorData.address_line_2 || null });
        }
        if (editorData.city !== originalEditorData.city) {
          operations.push({ op: 'replace', path: '/city', value: editorData.city || null });
        }
        if (editorData.state_province_region !== originalEditorData.state_province_region) {
          operations.push({ op: 'replace', path: '/state_province_region', value: editorData.state_province_region || null });
        }
        if (editorData.postal_code !== originalEditorData.postal_code) {
          operations.push({ op: 'replace', path: '/postal_code', value: editorData.postal_code || null });
        }
        if (!listsEqual(editorData.lists, originalEditorData.lists)) {
          operations.push({ op: 'replace', path: '/lists', value: editorData.lists });
        }
      }

      // If no changes, just close the modal
      if (operations.length === 0) {
        setShowEditorModal(false);
        setShowEditorResubscribeConfirm(false);
        return;
      }

      try {
        // Use original ID in the URL path (the contact's current ID in the database)
        await patchMarketingEmailContactsId(originalEditorData!.id, operations);

        queryClient.invalidateQueries({ queryKey: ['emailSubscriptions'] });
        queryClient.invalidateQueries({ queryKey: getGetMarketingEmailListsQueryKey() });
        setShowEditorModal(false);
        setShowEditorResubscribeConfirm(false);
        setUploadSuccess('Contact updated successfully');
      } catch (error: any) {
        const apiError = error?.response?.data?.message || error?.message || 'Failed to update contact';
        setUploadError(apiError);
      }
    }
  };

  // Handle editor resubscribe confirmation
  const handleEditorResubscribeConfirm = () => {
    setShowEditorResubscribeConfirm(false);
    handleSaveEditor();
  };

  const handleEditorResubscribeCancel = () => {
    setShowEditorResubscribeConfirm(false);
    // Revert the unsubscribed change to original value
    if (originalEditorData) {
      setEditorData({ ...editorData, unsubscribed: originalEditorData.unsubscribed });
    }
  };

  // Get cell text for tooltip
  const getCellText = (subscription: EmailContact, columnKey: EmailContactColumnKey): string => {
    switch (columnKey) {
      case 'email': return subscription.email || '';
      case 'id': return subscription.id?.toString() || '';
      case 'unsubscribed': return subscription.unsubscribed ? 'Unsubscribed' : 'Subscribed';
      case 'first_name': return subscription.first_name || '';
      case 'last_name': return subscription.last_name || '';
      case 'address_line_1': return subscription.address_line_1 || '';
      case 'address_line_2': return subscription.address_line_2 || '';
      case 'city': return subscription.city || '';
      case 'state_province_region': return subscription.state_province_region || '';
      case 'postal_code': return subscription.postal_code || '';
      case 'created_at': return subscription.created_at ? new Date(subscription.created_at).toLocaleString() : '';
      default: return '';
    }
  };

  // Column definitions for DataTable
  const columns: ColumnConfig<EmailContact>[] = [
    {
      key: 'id',
      label: 'ID',
      sortable: true,
      width: 280,
      renderCell: (contact) => <code style={{ fontSize: '0.85em' }}>{contact.id}</code>,
      tooltip: (contact) => contact.id || '',
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      width: 300,
      renderCell: (contact) => (
        <div
          className="fw-semibold"
          style={{ cursor: 'pointer', color: '#0d6efd' }}
          onClick={() => handleEditContact(contact)}
          title="Click to edit contact"
        >
          {contact.email}
        </div>
      ),
      tooltip: (contact) => contact.email || '',
    },
    {
      key: 'lists',
      label: 'Lists',
      sortable: false,
      width: 200,
      renderCell: (contact) => (
        <div className="d-flex flex-wrap gap-1">
          {(contact.lists || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((list, idx) => (
            <span
              key={list.id || idx}
              style={{
                backgroundColor: '#d4edbc',
                color: '#11734b',
                padding: '0.2em 0.5em',
                borderRadius: '0.25rem',
                fontSize: '0.8em',
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}
            >
              {list.name}
            </span>
          ))}
        </div>
      ),
      tooltip: (contact) => (contact.lists || []).map(l => l.name).join(', '),
    },
    {
      key: 'unsubscribed',
      label: 'Status',
      sortable: true,
      width: 120,
      renderCell: (contact) => (
        <span
          className={`badge ${contact.unsubscribed ? 'bg-danger' : 'bg-success'}`}
          style={{ cursor: 'pointer' }}
          onClick={() => handleStatusClick(contact)}
          title="Click to change status"
        >
          {contact.unsubscribed ? 'Unsubscribed' : 'Subscribed'}
        </span>
      ),
      tooltip: (contact) => contact.unsubscribed ? 'Unsubscribed' : 'Subscribed',
    },
    {
      key: 'first_name',
      label: 'First Name',
      sortable: true,
      width: 150,
      renderCell: (contact) => renderEditableCell(contact, 'first_name'),
      tooltip: (contact) => contact.first_name || '',
    },
    {
      key: 'last_name',
      label: 'Last Name',
      sortable: true,
      width: 150,
      renderCell: (contact) => renderEditableCell(contact, 'last_name'),
      tooltip: (contact) => contact.last_name || '',
    },
    {
      key: 'address_line_1',
      label: 'Address 1',
      sortable: false,
      width: 200,
      renderCell: (contact) => renderEditableCell(contact, 'address_line_1'),
      tooltip: (contact) => contact.address_line_1 || '',
    },
    {
      key: 'address_line_2',
      label: 'Address 2',
      sortable: false,
      width: 200,
      renderCell: (contact) => renderEditableCell(contact, 'address_line_2'),
      tooltip: (contact) => contact.address_line_2 || '',
    },
    {
      key: 'city',
      label: 'City',
      sortable: true,
      width: 150,
      renderCell: (contact) => renderEditableCell(contact, 'city'),
      tooltip: (contact) => contact.city || '',
    },
    {
      key: 'state_province_region',
      label: 'State/Province',
      sortable: true,
      width: 120,
      renderCell: (contact) => renderEditableCell(contact, 'state_province_region'),
      tooltip: (contact) => contact.state_province_region || '',
    },
    {
      key: 'postal_code',
      label: 'Postal Code',
      sortable: true,
      width: 120,
      renderCell: (contact) => renderEditableCell(contact, 'postal_code'),
      tooltip: (contact) => contact.postal_code || '',
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 150,
      renderCell: (contact) => contact.created_at ? new Date(contact.created_at).toLocaleDateString() : '-',
      tooltip: (contact) => contact.created_at ? new Date(contact.created_at).toLocaleString() : '',
    },
  ];

  // Helper to render editable cells
  const renderEditableCell = (contact: EmailContact, columnKey: EmailContactColumnKey) => {
    const isEditing = editingCell?.id === contact.id && editingCell?.column === columnKey;

    if (isEditing) {
      return (
        <div className="d-flex align-items-center gap-1">
          <Form.Control
            type="text"
            size="sm"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSaveEdit}
            autoFocus
            style={{ fontSize: '0.875rem' }}
          />
        </div>
      );
    }

    return (
      <div
        style={{ cursor: 'pointer' }}
        onClick={() => handleCellClick(contact, columnKey)}
        title="Click to edit"
      >
        {getCellText(contact, columnKey)}
      </div>
    );
  };

  return (
    <>

        {/* Alerts */}
        {uploadError && <Alert variant="danger" dismissible onClose={() => setUploadError(null)} className="mb-4">{uploadError}</Alert>}
        {uploadSuccess && <Alert variant="success" dismissible onClose={() => setUploadSuccess(null)} className="mb-4">{uploadSuccess}</Alert>}

        {/* Stats Cards - Subscription Status */}
        <h6 className="text-muted mb-2">Subscription Status</h6>
        <Row className="mb-3">
          <Col md={4}>
            <Card>
              <Card.Body className="py-2">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0 small">Total Contacts</p>
                    <h4 className="mb-0">{stats.total}</h4>
                  </div>
                  <i className="bi bi-envelope fs-3 text-primary"></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card>
              <Card.Body className="py-2">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0 small">Subscribed</p>
                    <h4 className="mb-0 text-success">{stats.subscribed}</h4>
                  </div>
                  <i className="bi bi-envelope-check fs-3 text-success"></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card>
              <Card.Body className="py-2">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0 small">Unsubscribed</p>
                    <h4 className="mb-0 text-danger">{stats.unsubscribed}</h4>
                  </div>
                  <i className="bi bi-envelope-x fs-3 text-danger"></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Subscriptions Table */}
            <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '240px' }}>
              <Card.Header className="bg-primary text-white flex-shrink-0">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="bi bi-table me-2"></i>
                    Contacts ({totalCount ?? subscriptions.length})
                  </h5>
                  <div className="d-flex align-items-center gap-2">
                    <div className="bg-header-control rounded px-1 py-1" data-bs-theme="light">
                      <Form.Control
                        type="text"
                        placeholder="Filter by email..."
                        value={emailFilter}
                        onChange={(e) => setEmailFilter(e.target.value)}
                        size="sm"
                        style={{ width: '200px' }}
                      />
                    </div>
                    <div className="d-flex align-items-center gap-2 bg-header-control rounded px-2 py-1">
                      <div className="d-flex align-items-center">
                        <Dropdown autoClose="outside">
                          <Dropdown.Toggle
                            variant={listFilter.length > 0 ? 'primary' : 'outline-secondary'}
                            size="sm"
                            style={listFilter.length > 0 ? { borderTopRightRadius: 0, borderBottomRightRadius: 0 } : undefined}
                          >
                            <i className="bi bi-funnel me-1"></i>
                            Lists{listFilter.length > 0 && ` (${listFilter.length})`}
                          </Dropdown.Toggle>
                        <Dropdown.Menu className="bg-header-control" data-bs-theme="light" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          {(emailLists || []).length === 0 ? (
                            <Dropdown.ItemText className="text-muted">No lists available</Dropdown.ItemText>
                          ) : (
                            (emailLists || [])
                              .slice()
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((list) => (
                                <Dropdown.Item
                                  key={list.id}
                                  onClick={() => {
                                    if (listFilter.includes(list.id)) {
                                      setListFilter(listFilter.filter(id => id !== list.id));
                                    } else {
                                      setListFilter([...listFilter, list.id]);
                                    }
                                  }}
                                >
                                  <i className={`bi ${listFilter.includes(list.id) ? 'bi-check-square-fill text-primary' : 'bi-square'} me-2`}></i>
                                  {list.name}
                                </Dropdown.Item>
                              ))
                          )}
                          {listFilter.length > 0 && (
                            <>
                              <Dropdown.Divider />
                              <Dropdown.Item onClick={() => setListFilter([])}>
                                <i className="bi bi-x-circle me-2"></i>
                                Clear all
                              </Dropdown.Item>
                            </>
                          )}
                        </Dropdown.Menu>
                        </Dropdown>
                        {listFilter.length > 0 && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => setListFilter([])}
                            style={{
                              borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0,
                              borderLeft: '1px solid rgba(255,255,255,0.3)',
                            }}
                            title="Clear list filter"
                          >
                            <i className="bi bi-x"></i>
                          </Button>
                        )}
                      </div>
                      <div className="btn-group btn-group-sm" role="group">
                        <Button
                          variant={listMatchMode === 'any' ? 'primary' : 'outline-secondary'}
                          onClick={() => setListMatchMode('any')}
                          title="Show contacts in ANY of the selected lists"
                        >
                          Any
                        </Button>
                        <Button
                          variant={listMatchMode === 'all' ? 'primary' : 'outline-secondary'}
                          onClick={() => setListMatchMode('all')}
                          title="Show contacts in ALL of the selected lists"
                        >
                          All
                        </Button>
                      </div>
                      <div className="btn-group btn-group-sm" role="group">
                        <Button
                          variant={statusFilter === 'all' ? 'primary' : 'outline-secondary'}
                          onClick={() => setStatusFilter('all')}
                          title="Show all contacts"
                        >
                          All
                        </Button>
                        <Button
                          variant={statusFilter === 'subscribed' ? 'success' : 'outline-secondary'}
                          onClick={() => setStatusFilter('subscribed')}
                          title="Show only subscribed contacts"
                        >
                          Subscribed
                        </Button>
                        <Button
                          variant={statusFilter === 'unsubscribed' ? 'danger' : 'outline-secondary'}
                          onClick={() => setStatusFilter('unsubscribed')}
                          title="Show only unsubscribed contacts"
                        >
                          Unsubscribed
                        </Button>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 bg-header-control rounded px-2 py-1">
                      <Button variant="primary" size="sm" onClick={handleAddContact}>
                        <i className="bi bi-plus-circle me-1"></i>
                        Add
                      </Button>
                      <Button variant="outline-primary" size="sm" onClick={() => setShowImportInfoModal(true)}>
                        <i className="bi bi-upload me-1"></i>
                        Import
                      </Button>
                      <Button variant="outline-primary" size="sm" onClick={() => {
                        setOriginalExportFieldNames({ ...exportFieldNames });
                        setShowExportModal(true);
                      }}>
                        <i className="bi bi-download me-1"></i>
                        Export
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => setShowBulkDeleteModal(true)}>
                        <i className="bi bi-trash me-1"></i>
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </Card.Header>
              <Card.Body
                className="flex-grow-1 overflow-hidden"
                style={{
                  padding: 0,
                  borderBottomLeftRadius: 'inherit',
                  borderBottomRightRadius: 'inherit',
                }}
              >
                <DataTable
                  data={subscriptions}
                  columns={columns}
                  getRowKey={getRowKey}
                  sortConfig={{ field: sortField, direction: sortDirection }}
                  onSortChange={handleSortChange}
                  pagination={{
                    hasMore,
                    limit: FETCH_LIMIT,
                  }}
                  onLoadMore={handleLoadMore}
                  isLoading={isLoading}
                  preferencesKey="email_subscriptions"
                  userId={user?.id}
                  userPreferences={userData?.preferences}
                  maxHeight="100%"
                  enableColumnReorder={true}
                  enableColumnResize={true}
                  enableColumnVisibility={true}
                  enableStickyHeader={true}
                  emptyState={
                    <Alert variant="info" className="m-3">
                      {(() => {
                        const filters = [];
                        if (debouncedEmailFilter) filters.push(`email "${debouncedEmailFilter}"`);
                        if (listFilter.length > 0) {
                          const listNames = listFilter
                            .map(id => emailLists?.find(l => l.id === id)?.name)
                            .filter(Boolean);
                          if (listNames.length > 0) {
                            filters.push(`list${listNames.length > 1 ? 's' : ''} "${listNames.join('", "')}"`);
                          }
                        }
                        return filters.length > 0
                          ? `No contacts found matching ${filters.join(' and ')}.`
                          : 'No contacts found.';
                      })()}
                    </Alert>
                  }
                />
              </Card.Body>
            </Card>

      {/* Resubscribe Confirmation Modal (from datatable status click) */}
      <ResubscribeConfirmModal
        show={showResubscribeConfirm}
        email={pendingResubscribe?.email || ''}
        onConfirm={handleConfirmResubscribe}
        onCancel={handleCancelResubscribe}
      />

      {/* Resubscribe Confirmation Modal (from editor modal) */}
      <ResubscribeConfirmModal
        show={showEditorResubscribeConfirm}
        email={editorData.email}
        onConfirm={handleEditorResubscribeConfirm}
        onCancel={handleEditorResubscribeCancel}
      />

      {/* Contact Editor Modal */}
      <Modal show={showEditorModal} onHide={() => setShowEditorModal(false)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{editorMode === 'create' ? 'Add Contact' : 'Edit Contact'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {editorMode === 'edit' && (
              <Form.Group className="mb-3">
                <Form.Label>ID</Form.Label>
                <Form.Control
                  type="text"
                  value={editorData.id || ''}
                  onChange={(e) => setEditorData({ ...editorData, id: e.target.value })}
                  placeholder="Contact ID (used for SendGrid external_id)"
                />
                <Form.Text className="text-muted">
                  Change this to match SendGrid's external_id if there's a mismatch.
                </Form.Text>
              </Form.Group>
            )}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    value={editorData.email}
                    onChange={(e) => setEditorData({ ...editorData, email: e.target.value })}
                    disabled={editorMode === 'edit'}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Subscription Status</Form.Label>
                  <Form.Check
                    type="checkbox"
                    id="editor-unsubscribed-checkbox"
                    label="Unsubscribed"
                    checked={editorData.unsubscribed}
                    onChange={(e) => setEditorData({ ...editorData, unsubscribed: e.target.checked })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editorData.first_name}
                    onChange={(e) => setEditorData({ ...editorData, first_name: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editorData.last_name}
                    onChange={(e) => setEditorData({ ...editorData, last_name: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Address Line 1</Form.Label>
              <Form.Control
                type="text"
                value={editorData.address_line_1}
                onChange={(e) => setEditorData({ ...editorData, address_line_1: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Address Line 2</Form.Label>
              <Form.Control
                type="text"
                value={editorData.address_line_2}
                onChange={(e) => setEditorData({ ...editorData, address_line_2: e.target.value })}
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    value={editorData.city}
                    onChange={(e) => setEditorData({ ...editorData, city: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>State/Province</Form.Label>
                  <Form.Control
                    type="text"
                    value={editorData.state_province_region}
                    onChange={(e) => setEditorData({ ...editorData, state_province_region: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={editorData.postal_code}
                    onChange={(e) => setEditorData({ ...editorData, postal_code: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <hr className="my-4" />

            {/* Email Lists Multiselect */}
            <Form.Group className="mb-3">
              <Form.Label>Email Lists</Form.Label>
              <ListSelector
                selectedLists={editorData.lists}
                onChange={(lists) => setEditorData({ ...editorData, lists })}
                placeholder="Select lists..."
              />
            </Form.Group>
          </Form>

          {/* Fixed notification area */}
          <div style={{ minHeight: '56px', marginTop: '1rem' }}>
            {modalError && (
              <Alert variant="danger" dismissible onClose={() => setModalError(null)} className="mb-0">
                {modalError}
              </Alert>
            )}
            {modalSuccess && (
              <Alert variant="success" dismissible onClose={() => setModalSuccess(null)} className="mb-0">
                {modalSuccess}
              </Alert>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          {editorMode === 'edit' && !showDeleteConfirm && (
            <Button variant="outline-danger" className="me-auto" onClick={() => setShowDeleteConfirm(true)}>
              <i className="bi bi-trash me-1"></i>
              Delete
            </Button>
          )}
          {editorMode === 'edit' && showDeleteConfirm && (
            <div className="me-auto d-flex align-items-center gap-2">
              <span className="text-danger">Delete this contact?</span>
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  if (editorData.email) {
                    deleteSingleContactMutation.mutate(editorData.email);
                  }
                }}
                disabled={deleteSingleContactMutation.isPending}
              >
                {deleteSingleContactMutation.isPending ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  'Yes, Delete'
                )}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setShowDeleteConfirm(false)}>
                No
              </Button>
            </div>
          )}
          <Button variant="secondary" onClick={() => { setShowEditorModal(false); setShowDeleteConfirm(false); }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveEditor}
            disabled={!editorData.email || createSubscriptionMutation.isPending || updateSubscriptionMutation.isPending || showDeleteConfirm}
          >
            {createSubscriptionMutation.isPending || updateSubscriptionMutation.isPending ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              editorMode === 'create' ? 'Add Contact' : 'Save Changes'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* CSV Upload Modal */}
      <Modal show={showCsvUploadModal} onHide={() => setShowCsvUploadModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Import Contacts from CSV</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {csvUploadError && (
            <Alert variant="danger" dismissible onClose={() => setCsvUploadError(null)} className="mb-3">
              {csvUploadError}
            </Alert>
          )}

          <div className="mb-3">
            <strong>File:</strong> {csvFile?.name}
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Add all imported contacts to lists (optional)</Form.Label>
            <ListSelector
              selectedLists={csvUploadLists}
              onChange={setCsvUploadLists}
              placeholder="Select lists to add contacts to..."
            />
            <Form.Text className="text-muted">
              These lists are in addition to any lists specified in the CSV's 'lists' column.
              Lists in the CSV that don't exist will be created automatically.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCsvUploadModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCsvUpload}
            disabled={bulkImportMutation.isPending}
          >
            {bulkImportMutation.isPending ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Importing...
              </>
            ) : (
              <>
                <i className="bi bi-upload me-2"></i>
                Import
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Import Info Modal */}
      <Modal show={showImportInfoModal} onHide={() => setShowImportInfoModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Import Contacts from CSV</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {csvUploadError && (
            <Alert variant="danger" dismissible onClose={() => setCsvUploadError(null)} className="mb-3">
              {csvUploadError}
            </Alert>
          )}

          <p className="mb-3">
            Upload a CSV file to import contacts. The CSV format should include:
          </p>
          <ul className="mb-3">
            <li><strong>email</strong> (required) - Contact email address</li>
            <li><em>arrayscape_id</em> - Unique identifier (auto-generated if not provided)</li>
            <li><em>unsubscribed</em> - Subscription status (true/false, 1/0, or yes/no)</li>
            <li><em>first_name</em> - First name</li>
            <li><em>last_name</em> - Last name</li>
            <li><em>address_line_1</em> - Street address</li>
            <li><em>address_line_2</em> - Apt/Suite/etc.</li>
            <li><em>city</em> - City</li>
            <li><em>state_province_region</em> - State or province</li>
            <li><em>postal_code</em> - ZIP/Postal code</li>
            <li><em>lists</em> - Comma-separated list names to add contact to</li>
          </ul>

          <Form.Group className="mb-3">
            <Form.Label>Add all imported contacts to lists (optional)</Form.Label>
            <ListSelector
              selectedLists={csvUploadLists}
              onChange={setCsvUploadLists}
              placeholder="Select lists to add contacts to..."
            />
            <Form.Text className="text-muted">
              These lists are in addition to any lists specified in the CSV's 'lists' column.
              Lists in the CSV that don't exist will be created automatically.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImportInfoModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={bulkImportMutation.isPending}
            onClick={() => document.getElementById('csvFileInput')?.click()}
          >
            {bulkImportMutation.isPending ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Importing...
              </>
            ) : (
              <>
                <i className="bi bi-upload me-2"></i>
                Upload CSV
              </>
            )}
          </Button>
          <input
            id="csvFileInput"
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              if (!file.name.endsWith('.csv')) {
                setUploadError('Please upload a CSV file');
                return;
              }
              e.target.value = '';
              const listIds = csvUploadLists.filter(l => l.id).map(l => l.id!);
              const newListNames = csvUploadLists.filter(l => !l.id && l.name).map(l => l.name!);
              bulkImportMutation.mutate({
                file,
                listIds,
                newListNames,
              });
            }}
          />
        </Modal.Footer>
      </Modal>

      {/* Export Modal */}
      <Modal show={showExportModal} onHide={() => {
        setExportFieldNames(originalExportFieldNames);
        setShowExportModal(false);
      }} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Export Contacts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Select fields to include in the CSV export and optionally customize column names.
          </p>

          {/* Field Selection */}
          <Form>
            <div className="border rounded p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <strong>Export Fields</strong>
                <div>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 me-3"
                    onClick={() => {
                      const allSelected: Record<string, boolean> = {};
                      exportFieldOptions.forEach(f => { allSelected[f.key] = true; });
                      setExportFields(allSelected);
                    }}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 me-3"
                    onClick={() => {
                      const noneSelected: Record<string, boolean> = {};
                      exportFieldOptions.forEach(f => { noneSelected[f.key] = false; });
                      setExportFields(noneSelected);
                    }}
                  >
                    Clear All
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    onClick={() => {
                      const cleared: Record<string, string> = {};
                      exportFieldOptions.forEach(f => { cleared[f.key] = ''; });
                      setExportFieldNames(cleared);
                    }}
                  >
                    Reset Names
                  </Button>
                </div>
              </div>
              {/* Header row */}
              <Row className="mb-2 text-muted small">
                <Col xs={5}>Database Field</Col>
                <Col xs={7}>Export Column Name (optional)</Col>
              </Row>
              {/* Field rows */}
              {exportFieldOptions.map(field => (
                <Row key={field.key} className="mb-2 align-items-center">
                  <Col xs={5}>
                    <Form.Check
                      type="checkbox"
                      id={`export-field-${field.key}`}
                      label={field.key}
                      checked={exportFields[field.key]}
                      onChange={(e) => setExportFields(prev => ({ ...prev, [field.key]: e.target.checked }))}
                      style={{ fontFamily: 'monospace' }}
                    />
                  </Col>
                  <Col xs={7}>
                    <Form.Control
                      type="text"
                      size="sm"
                      value={exportFieldNames[field.key]}
                      onChange={(e) => setExportFieldNames(prev => ({ ...prev, [field.key]: e.target.value }))}
                      disabled={!exportFields[field.key]}
                      placeholder={field.key}
                      style={{ opacity: exportFields[field.key] ? 1 : 0.5 }}
                    />
                  </Col>
                </Row>
              ))}
            </div>
          </Form>

          {/* Current Filters Info */}
          <Alert variant="info" className="mb-0">
            <i className="bi bi-funnel me-2"></i>
            {(debouncedEmailFilter || listFilter.length > 0 || statusFilter !== 'all') ? (
              <>
                Current filters:{' '}
                {[
                  debouncedEmailFilter && `email "${debouncedEmailFilter}"`,
                  listFilter.length > 0 && `list${listFilter.length > 1 ? 's' : ''} "${listFilter.map(id => emailLists?.find(l => l.id === id)?.name || id).join('", "')}"`,
                  statusFilter !== 'all' && `status "${statusFilter}"`,
                ].filter(Boolean).join(', ')}
              </>
            ) : (
              'No filters applied - all contacts will be exported'
            )}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setExportFieldNames(originalExportFieldNames);
            setShowExportModal(false);
          }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              saveExportFieldMappings(exportFieldNames);
              handleExportFiltered();
              setShowExportModal(false);
            }}
            disabled={!Object.values(exportFields).some(v => v)}
          >
            <i className="bi bi-download me-2"></i>
            Export
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Delete Modal */}
      <Modal
        show={showBulkDeleteModal}
        onHide={() => {
          setShowBulkDeleteModal(false);
          setDeleteAllConfirmText('');
        }}
        centered
      >
        <Modal.Header closeButton className={!(debouncedEmailFilter || listFilter.length > 0 || statusFilter !== 'all') ? 'bg-warning' : ''}>
          <Modal.Title>
            {!(debouncedEmailFilter || listFilter.length > 0 || statusFilter !== 'all') ? (
              <>Delete ALL Contacts</>
            ) : (
              <>Delete Contacts</>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!(debouncedEmailFilter || listFilter.length > 0 || statusFilter !== 'all') ? (
            <>
              <Alert variant="warning" className="mb-3">
                <strong>WARNING:</strong> No filters are applied.
              </Alert>
              <p>
                This will delete <strong>ALL {totalCount ?? subscriptions.length}</strong> contacts.
              </p>
              <p className="text-danger mb-3">
                This action cannot be undone.
              </p>
              <Form.Group className="mb-0">
                <Form.Label>Type <strong>DELETE ALL</strong> to confirm:</Form.Label>
                <Form.Control
                  type="text"
                  value={deleteAllConfirmText}
                  onChange={(e) => setDeleteAllConfirmText(e.target.value)}
                  placeholder="DELETE ALL"
                />
              </Form.Group>
            </>
          ) : (
            <>
              <p className="mb-2">You are about to delete contacts matching these filters:</p>
              <ul className="mb-3">
                {debouncedEmailFilter && (
                  <li>Email contains: <strong>"{debouncedEmailFilter}"</strong></li>
                )}
                {listFilter.length > 0 && (
                  <li>
                    In {listMatchMode === 'all' ? 'all of' : 'any of'} list{listFilter.length > 1 ? 's' : ''}:{' '}
                    <strong>
                      {listFilter
                        .map(id => emailLists?.find(l => l.id === id)?.name || id)
                        .join(', ')}
                    </strong>
                  </li>
                )}
                {statusFilter !== 'all' && (
                  <li>Status: <strong>{statusFilter === 'subscribed' ? 'Subscribed' : 'Unsubscribed'}</strong></li>
                )}
              </ul>
              <p>
                This will delete <strong>{totalCount ?? subscriptions.length}</strong> contact{(totalCount ?? subscriptions.length) !== 1 ? 's' : ''}.
              </p>
              <p className="text-danger mb-0">
                This action cannot be undone.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowBulkDeleteModal(false);
              setDeleteAllConfirmText('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              deleteContactsMutation.mutate({
                email: debouncedEmailFilter || undefined,
                list_id: listFilter.length > 0 ? listFilter.join(',') : undefined,
                list_match: listFilter.length > 1 ? listMatchMode : undefined,
                subscribed: statusFilter !== 'all' ? statusFilter : undefined,
              });
            }}
            disabled={
              deleteContactsMutation.isPending ||
              (!(debouncedEmailFilter || listFilter.length > 0 || statusFilter !== 'all') && deleteAllConfirmText !== 'DELETE ALL')
            }
          >
            {deleteContactsMutation.isPending ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <i className="bi bi-trash me-2"></i>
                {!(debouncedEmailFilter || listFilter.length > 0 || statusFilter !== 'all')
                  ? 'Delete All'
                  : `Delete ${totalCount ?? subscriptions.length}`}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EmailContactsTab;
